import type { Vehicle } from '@cobliteam/common-types';
import useGlobalFilterStore from '@cobliteam/hooks/src/useGlobalFilterStore';
import { useGetPathAssociations } from '@cobliteam/hooks/src/usePathAssociation';
import DatePicker, { type DateRange } from '@cobliteam/ui/components/DatePicker';
import DataTable, {
  filterGetByKeys,
  filterGetParamDates,
  filterGetParamPagination,
  filterSetParamDates,
  filterSetParamFilters,
  type IFiltersDef,
  type TFilterKeys,
} from '@cobliteam/ui-business/components/DataTable';
import { differenceInDays, endOfDay, startOfDay } from '@cobliteam/utils/date';
import { useMemo, useState } from 'react';
import columnsDef from './columnsDef';
import DisabledFaceIdTag from './components/DisabledFaceIdTag';

const LIMIT_IN_DAYS = 7;

const PathAssociationTable = () => {
  const initialDate = filterGetParamDates(1, 1);
  const [selectedDate, setSelectedDate] = useState<DateRange>({
    from: initialDate.startDate,
    to: initialDate.endDate,
  });
  const [pagination, setPagination] = useState(filterGetParamPagination());
  const [search, setSearch] = useState('');
  const [isInvalidDateRange, setIsInvalidDateRange] = useState<boolean>(false);

  const vehicleGroupIds = useGlobalFilterStore((state) => state.vehicleGroupIds);

  const filterDate = useMemo(
    () => ({
      start: startOfDay(selectedDate.from ?? new Date()).getTime(),
      end: endOfDay(selectedDate.to ?? new Date()).getTime(),
    }),
    [selectedDate]
  );
  const filtersDef = filterGetByKeys(['MultiSelectVehicles']);

  const getFilterValue = <T,>(key: TFilterKeys, defaultValue: T): T => {
    const filter = filtersDef.find((it) => it.key === key);
    if (!filter?.value) return defaultValue;
    return filter.value;
  };

  const handleFilterChange = (filter: IFiltersDef) => {
    const filterDef = filtersDef.find((f) => f.key === filter.key) as IFiltersDef;
    const newFilter = {
      ...filterDef,
      ...filter,
    };

    filterSetParamFilters(newFilter);
  };

  const handleDateChange = (date: DateRange) => {
    const startDate = date.from ?? new Date();
    const endDate = date.to ?? new Date();

    if (differenceInDays(endDate, startDate) >= LIMIT_IN_DAYS) {
      setIsInvalidDateRange(true);
    } else {
      setIsInvalidDateRange(false);
      setSelectedDate(date);
      setPagination({
        pageIndex: 0,
        pageSize: pagination.pageSize,
      });
      filterSetParamDates({
        startDate: startDate,
        endDate: endDate,
      });
    }
  };

  const { data, isLoading, isFetching, refetch } = useGetPathAssociations(
    {
      period: filterDate,
      page: pagination.pageIndex,
      size: pagination.pageSize,
      searchQuery: search,
      wantedVehicleIds: getFilterValue<Vehicle[]>('MultiSelectVehicles', []).map((it) => it.id),
      wantedGroups: vehicleGroupIds,
    },
    { enabled: !isInvalidDateRange }
  );

  return (
    <DataTable
      id='path-association-table'
      columnsDef={columnsDef}
      filtersDef={filtersDef}
      manualPagination
      manualSorting
      manualFiltering
      showColumnFilter={false}
      data={data?.content}
      totalRows={data?.totalElements}
      pageCount={data?.totalPages}
      initialPagination={pagination}
      onChangePagination={setPagination}
      onChangeFilter={handleFilterChange}
      search={search}
      onChangeSearch={setSearch}
      datePicker={
        <DatePicker
          mode='range'
          selected={selectedDate}
          onSelect={handleDateChange}
          required
          disabledDays={{ after: new Date() }}
          presets={['today', 'yesterday', 'last-7-days']}
          max={LIMIT_IN_DAYS}
        />
      }
      isLoading={isLoading && !data}
      isFetching={isFetching}
      onRefresh={refetch}
      stats={() => <DisabledFaceIdTag />}
    />
  );
};

export default PathAssociationTable;
